import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import moment from "moment";
import { getLocale } from "react-i18nify";

import {
  getAllBlog,
  deleteBlogByAdmin,
  toggleBlogVisibility,
} from "src/api/blog";

import * as MatStyled from "@mui/material/styles";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import FormControlLabel from "@mui/material/FormControlLabel";

// import InputAdornment from "@mui/material/InputAdornment";
// import { makeStyles } from '@material-ui/core'

import { DataGrid } from "@mui/x-data-grid";
import { GridActionsCellItem } from "@mui/x-data-grid";

import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import ModalConfirm from "src/components/ui/ModalConfirm";
import Loading from "src/components/ui/Loading";
import { IOSSwitch } from "src/components/core/Switch";
import { useAuth } from "src/hooks/useAuth";

const DivMuiName = styled.div`
  width: 100%;
  height: auto;
  padding: 32px;
  border-radius: 32px;
  background-color: #ffffff;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  // display: flex;
  justify-content: center;
  align-items: center;
`;

const DivMuiInside = styled.div`
  width: 100%;
  margin-bottom: 20px;
  display: flex;
`;

const WrapperText = styled.div`
  // margin-bottom: 32px;
  width: 88%;
  position: relative;
  display: flex;
  justify-content: center;
  .TextField-without-border-radius fieldset {
    border-radius: 16px;
  }
`;

const WrapperButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0px;
  width: max-content;
  margin-left: auto;

  .Button-Add {
    color: #ffffff;
    width: max-content;
    background: #e04132;
    box-shadow:
      0px 3px 1px -2px rgba(0, 0, 0, 0.2),
      0px 2px 2px rgba(0, 0, 0, 0.14),
      0px 1px 5px rgba(0, 0, 0, 0.12);
    border-radius: 16px;
    height: 52px;
    padding: 16px 24px;
  }
  .Button-Add:focus {
    background-color: #e04132;
  }
  .Button-Add:hover {
    background-color: #e04132;
    opacity: 0.9;
  }
`;

const WrapperDataGrid = styled.div`
  height: 600px;
  width: 100%;
  .MuiDataGrid-root {
    border: 1px solid #ffffff;
  }
  .MuiDataGrid-columnHeaders {
    background-color: #f5f5f5;
  }
  .MuiDataGrid-root .MuiDataGrid-columnSeparator--sideRight {
    color: #f5f5f5;
  }
  .MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within {
    // outline: solid #f5f5f5 1px;
    outline: none;
  }
  .MuiDataGrid-root .MuiDataGrid-cell:focus {
    outline: none;
    border-bottom: solid #f5f5f5 1px 0 1px 0;
  }
  .MuiDataGrid-root .MuiDataGrid-row.Mui-selected {
    background-color: #ffffff;
  }
`;

const ImageNoData = styled.image`
  width: 120px;
  height: 120px;
  background-image: url("https://xsurface-images.s3.ap-southeast-1.amazonaws.com/no+data.svg");
  background-repeat: no-repeat;
  background-size: 100%;
`;
const ImageNotFound = styled.image`
  width: 120px;
  height: 120px;
  background-image: url("https://xsurface-images.s3.ap-southeast-1.amazonaws.com/not+found.svg");
  background-repeat: no-repeat;
  background-size: 100%;
`;

const TextNoData = styled.pre`
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
`;

const WrapperAlert = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 50px;
  z-index: 3000;
  animation: showAlert 0.5s;
  margin-top: 15px;
  display: flex;
  justify-content: center;
  @keyframes showAlert {
    0% {
      opacity: 1;
    }
    100% {
      opacity: 1;
    }
  }
`;

const DivAlert = styled.div`
  min-width: 320px;
  width: fit-content;
  .MuiPaper-root-MuiAlert-root {
    border-radius: 16px;
  }
`;

const drawerWidth = 260;

const Main = MatStyled.styled("main", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  position: "relative",
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  height: "100vh",
  overflow: "hidden",
  overflowY: "scroll",
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
    position: "relative",
  }),
}));

const DrawerHeader = MatStyled.styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
  height: "75px",
}));

function BlogList({ open }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [blogListData, setBlogListData] = useState([]);

  const initialConfirm = {
    visible: false,
    title: "",
    confirmFunc: () => {},
  };
  const [confirmInfo, setConfirmInfo] = useState(initialConfirm);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [dataSearch, setDataSearch] = useState({
    keyword: null,
    page: 0,
    page_size: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const initialAlert = {
    visible: false,
    textError: "",
    typeError: "success",
  };
  const [alertInfo, setAlertInfo] = useState(initialAlert);

  // function CustomPagination() {
  //   const apiRef = useGridApiContext();
  //   const page = useGridSelector(apiRef, gridPageSelector);
  //   const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  //   return (
  //     <WrapperPagination>
  //       <Pagination
  //         color="primary"
  //         count={pageCount}
  //         page={page + 1}
  //         onChange={(event, value) => apiRef.current.setPage(value - 1)}
  //       />
  //     </WrapperPagination>
  //   );
  // }

  const handleToggleVisibility = (blogId) => {
    setIsLoading(true);
    (async () => {
      if (user.role_id === 1) {
        await toggleBlogVisibility(blogId);
        const { data } = await getAllBlog({ ...dataSearch, page: 1 });
        setBlogListData(data.rows);
        setRowCount(data.count);
        setIsDataLoading(false);
      }
    })();
    setIsLoading(false);
  };

  const columns = [
    {
      field: "title",
      headerName: "Name",
      flex: 1.5,
      // width: 200,
      headerAlign: "center",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              margin: "8px",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              outline: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <div style={{ margin: "8px", width: "100%" }}>
              <div
                className="MuiDataGrid-cellContent"
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minHeight: "50px",
                }}
              >
                {params.row?.title ? params.row?.title : "-"}
              </div>
            </div>
          </Box>
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Create Date",
      flex: 1,
      headerAlign: "center",
      align: "center",
      editable: false,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <div className="rowitem">
            {params?.row?.createdAt
              ? moment(params?.row?.createdAt).format("DD-MM-YYYY")
              : "-"}
          </div>
        );
      },
    },
    {
      field: "updatedAt",
      headerName: "Update Date",
      flex: 1,
      headerAlign: "center",
      align: "center",
      editable: false,
      sortable: true,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <div className="rowitem">
            {params?.row?.updatedAt
              ? moment(params?.row?.updatedAt).format("DD-MM-YYYY")
              : "-"}
          </div>
        );
      },
    },
    {
      field: "visibility",
      headerName: "Public",
      flex: 1,
      headerAlign: "center",
      align: "center",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <WrapperButton>
            <FormControlLabel
              control={
                <IOSSwitch
                  sx={{ m: 1 }}
                  id={params?.row?._id}
                  checked={params?.row?.visibility === "public" ? true : false}
                  onChange={() => {
                    handleToggleVisibility(params?.row?._id);
                  }}
                />
              }
            />
          </WrapperButton>
        );
      },
    },
    {
      field: "edit",
      type: "actions",
      headerName: "Edit",
      flex: 1,
      headerAlign: "center",
      align: "center",
      editable: false,
      sortable: false,
      disableColumnMenu: true,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<VisibilityIcon />}
          label="View"
          onClick={() =>
            window.open(
              `https://xsurface.com/blog/${params.row?._id}`,
              "_blank",
            )
          }
          showInMenu
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => navigate(`/blog/edit/${params.row?._id}`)}
          showInMenu
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => {
            setConfirmInfo((info) => ({
              ...info,
              visible: true,
              title:
                getLocale() === "th"
                  ? `คุณต้องการลบบทความใช่หรือไม่\nหากกดยืนยันแล้วจะไม่สามารถแก้ไขได้อีก`
                  : `คุณต้องการลบบทความใช่หรือไม่\nหากกดยืนยันแล้วจะไม่สามารถแก้ไขได้อีก`,
              confirmFunc: () => deleteBlog(params.row?._id),
            }));
          }}
          showInMenu
        />,
      ],
    },
  ];

  function NoRowsOverlay() {
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        <ImageNoData></ImageNoData>
        <TextNoData>No Data</TextNoData>
      </Stack>
    );
  }

  function NoResultsOverlay() {
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        <ImageNotFound></ImageNotFound>
        <TextNoData>Not Found</TextNoData>
      </Stack>
    );
  }

  const onSearchButton = () => {
    setDataSearch({ ...dataSearch, page: 1 });
    setIsDataLoading(true);
    (async () => {
      if (user.role_id === 1) {
        const { data } = await getAllBlog({ ...dataSearch, page: 1 });
        setBlogListData(data.rows);
        setRowCount(data.count);
        setIsDataLoading(false);
      }
    })();
  };

  const handlePageChange = (newPage) => {
    // We have the cursor, we can allow the page transition.
    setDataSearch({ ...dataSearch, page: newPage});
  };

  const deleteBlog = (id) => {
    if (id === null || id === undefined) return;
    setIsLoading(true);

    deleteBlogByAdmin(id)
      .then((res) => {
        setIsLoading(false);
        setAlertInfo({
          ...alertInfo,
          visible: true,
          textError: "Delete Success!",
          typeError: "success",
        });
        setIsDataLoading(true);
        (async () => {
          const { data } = await getAllBlog({ ...dataSearch });
          setBlogListData(data.rows);
          setRowCount(data.count);
          setTimeout(() => {
            setIsDataLoading(false);
            setAlertInfo(initialAlert);
          }, 1500);
        })();
      })
      .catch((err) => {
        setIsLoading(false);
        setAlertInfo({
          ...alertInfo,
          visible: true,
          textError: err.response.data.message,
          typeError: "error",
        });
        setTimeout(() => {
          setAlertInfo(initialAlert);
        }, 1500);
      });
    setConfirmInfo(initialConfirm);
  };

  useEffect(() => {
    setIsDataLoading(true);
    (async () => {
      if (user.role_id === 1) {
        const { data } = await getAllBlog(dataSearch);
        setBlogListData(data.rows);
        setRowCount(data.count);
        setIsDataLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSearch.page, dataSearch.status]);

  return (
    <>
      <Main open={open}>
        <DrawerHeader />
        <DivMuiName>
          <DivMuiInside>
            <WrapperText>
              <TextField
                id="search-input"
                placeholder="ค้นหา"
                className="TextField-without-border-radius"
                sx={{
                  m: 1,
                  width: "100%",
                  borderRadius: "16px",
                }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      id="button-search-sample"
                      sx={{ padding: 0 }}
                      onClick={() => {
                        onSearchButton();
                      }}
                    >
                      <SearchIcon
                        sx={{ color: "#e04132", cursor: "pointer" }}
                      />
                    </IconButton>
                  ),
                }}
                onChange={(e) => {
                  setDataSearch({ ...dataSearch, keyword: e.target.value });
                }}
                onKeyUp={(e) => (e.code === "Enter" ? onSearchButton() : null)}
              />
            </WrapperText>

            <WrapperButton>
              <Button
                className="Button-Add"
                startIcon={<AddIcon />}
                onClick={() => navigate("/blog/create")}
              >
                Blog
              </Button>
            </WrapperButton>
          </DivMuiInside>

          <WrapperDataGrid>
            <DataGrid
              rows={blogListData}
              getRowId={(row) => row._id}
              getRowHeight={() => "auto"}
              loading={isDataLoading}
              columns={columns}
              pageSize={10}
              rowCount={rowCount}
              paginationMode="server"
              page={dataSearch.page}
              onPageChange={(e) => handlePageChange(e)}
              rowsPerPageOptions={[10]}
              pagination
              disableSelectionOnClick
              // localeText={{
              //   footerRowSelected: CustomPagination,
              // }}
              components={{
                // Footer: CustomPagination,
                NoRowsOverlay,
                NoResultsOverlay,
              }}
              componentsProps={{
                basePopper: {},
              }}
              sx={{
                "& .MuiDataGrid-actionsCell": {
                  transform: "rotate(90deg)",
                },
              }}
            />
          </WrapperDataGrid>
        </DivMuiName>

        {confirmInfo.visible ? (
          <ModalConfirm
            title={confirmInfo.title}
            callBack={(value) =>
              value === false
                ? setConfirmInfo(initialConfirm)
                : confirmInfo?.confirmFunc()
            }
            textCancel={getLocale() === "th" ? "ยกเลิก" : "cancel"}
            textConfirm={getLocale() === "th" ? "ลบ" : "delete"}
          />
        ) : null}

        {alertInfo.visible && (
          <WrapperAlert>
            <DivAlert>
              <Alert
                className="MuiPaper-root-MuiAlert-root"
                severity={alertInfo.typeError}
              >
                {alertInfo.textError}
              </Alert>
            </DivAlert>
          </WrapperAlert>
        )}
      </Main>

      {isLoading && <Loading />}
    </>
  );
}

export default BlogList;
